// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@ces/App/Components/Qty/QtyField";
import { mergeWithCss, xMask } from "@ces/XDL/Utilities";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    label?: string;
    // XNG-Changes
    extendedStyles?: QtyFieldStyles;
    showLabel?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    showLabel,
}) => {
    // Xng-Changes
    const minQty = Math.max(Number(cartLine.properties.minimumOrderQty || 1), 1); // Same as ProductQuantityOrdered component
    // XNG-Changes
    const packageSize = cartLine.qtyPerBaseUnitOfMeasure || Number(cartLine.properties.minimumOrderQty) || 1;

    const showQtyTooltip =
        (cartLine.properties.minimumOrderQty && Number(cartLine.properties.minimumOrderQty) > 1) ||
        (cartLine.properties.multipleSaleQty && Number(cartLine.properties.multipleSaleQty) > 1);

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }
        // XNG-Changes: prevent entering 0 qty on cart, or else the item will be deleted before blur changes it to minimum or 1
        onQtyOrderedChange(inputQty || Math.max(minQty, packageSize));
    };

    // XNG-Changes
    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // XNG-Changes
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (value && Number(xMask.qty(value)) === 0) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        blurHandler();
    };

    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <QtyField
                {...styles.editableQuantityTextField}
                showLabel={showLabel}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                onClick={event => event.currentTarget.focus()}
                showMultipleSaleQtyTooltip={!!showQtyTooltip}
                minimumOrderQty={Number(cartLine.properties.minimumOrderQty)}
                multipleSaleQty={Number(cartLine.properties.multipleSaleQty)}
                data-test-selector="cartline_qty"
                extendedStyles={extendedStyles}
                // XNG-Change
                min={minQty}
                qtyPerBaseUnitOfMeasure={cartLine.qtyPerBaseUnitOfMeasure}
            />
        );
    }

    // XNG-changes: Using QtyField Component with editable={false} to display the number as text
    return (
        <QtyField
            editable={false}
            value={value}
            showMultipleSaleQtyTooltip={!!showQtyTooltip}
            minimumOrderQty={Number(cartLine.properties.minimumOrderQty)}
            multipleSaleQty={Number(cartLine.properties.multipleSaleQty)}
            extendedStyles={extendedStyles}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
